import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Drawer } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import CampusDashboard from "shared/components/campus";
import HeadOfficeDashboard from "shared/components/headOffice";
import HeadOfficeDashboardSkeleton from "shared/components/headOffice/Skeleton";
import { HeadOfficeThemeProvider } from "shared/components/headOffice/theme/HeadOfficeThemeContext";
import {
  PaymentRecordsTable,
  PlanCompareModal,
  PlanHistoryTable,
  ReasonModal,
  ReceiptModal
} from "shared/components/schoolAnalytics";
import { setSchoolScopeId } from "shared/services/schoolScope";
import useSchoolDetail from "pages/schoolAnalyticsDetail/useSchoolDetail";
import useSchoolPlans, {
  HISTORY_PAGE_SIZE,
  toCampuses,
  toCompareRows
} from "pages/schoolAnalyticsDetail/useSchoolPlans";
import CustomButton from "shared/components/customButton";
import Confirmation from "shared/modals/confirmation";
import UpdateSchoolPlan from "shared/modals/updateSchoolPlan";
import Loader from "shared/components/loader/animation";
import { toastMessage } from "shared/components/toast";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import { routeConstant } from "shared/routes/routesConstant";
import {
  getSchoolOverviewService,
  reviewDemoRequestService,
  getBookFilterCountsService,
  editLimit
} from "shared/services/adminService";
import baseStyles from "../detail/style.module.scss";
import styles from "./style.module.scss";
import SkeletonLoader from "./skeletonLoader";

const statusConfig: Record<number, { label: string; color: string }> = {
  0: { label: "New", color: "#2196f3" },
  1: { label: "Pending", color: "#ff9800" },
  2: { label: "Approved", color: "#4caf50" },
  3: { label: "Rejected", color: "#f44336" },
  4: { label: "Expired", color: "#9e9e9e" },
  5: { label: "Active", color: "#4caf50" },
  6: { label: "Inactive", color: "#9e9e9e" }
};

function DemoRequestSchoolDetail() {
  const { id } = useParams();
  // The route carries the school's account id, which is the same id the plan
  // and limit endpoints on this screen call `school_id` — so it is what the
  // reading dashboards below are scoped to.
  const schoolId = Number(id);

  // Set during render, not in an effect: the dashboard's widgets fire their
  // requests on mount, which happens before any effect here would run.
  setSchoolScopeId(Number.isFinite(schoolId) ? schoolId : undefined);

  // Clear the scope on the way out so a later screen can't inherit it.
  useEffect(() => () => setSchoolScopeId(undefined), []);

  // Which dashboard this school gets, resolved from the same health-stat
  // lookup the Project Head's school detail screen uses.
  const { school: analyticsSchool, loading: analyticsLoading } =
    useSchoolDetail(schoolId);

  // The money side of the same school: its plans and the payments made against
  // them, read from the two routes the school panel's own Membership Plans
  // screen uses. Each table pages server-side on its own.
  const {
    plans,
    plansLoading,
    plansPage,
    plansTotal,
    setPlansPage,
    payments,
    paymentsLoading,
    paymentsPage,
    paymentsTotal,
    setPaymentsPage,
    receipts,
    rawPlans,
    planReceipts,
    currency
  } = useSchoolPlans(schoolId);

  /* Both open in a dialog rather than a new tab, and they stay separate: the
     eye button shows the receipt image, the row's flag shows why the payment
     was rejected. */
  const [openReceiptId, setOpenReceiptId] = useState<string | null>(null);
  const [openReasonId, setOpenReasonId] = useState<string | null>(null);

  /* The plan table's own two buttons: its details, and the receipt of the
     payment made against it. */
  const [openPlanId, setOpenPlanId] = useState<string | null>(null);
  const [openPlanReceiptId, setOpenPlanReceiptId] = useState<string | null>(
    null
  );

  const [item, setItem] = useState<any>(null);
  const [pageLoader, setPageLoader] = useState<boolean>(true);
  const [rejectLoader, setRejectLoader] = useState<boolean>(false);
  const [languages, setLanguages] = useState<any[]>([]);
  const [ageGroups, setAgeGroups] = useState<any[]>([]);
  // Raw language + age + books_count combinations from the
  // books/filter-counts endpoint.
  const [bookFilterCounts, setBookFilterCounts] = useState<any[]>([]);
  const [openNotes, setOpenNotes] = useState<boolean>(false);
  const [notesList, setNotesList] = useState<string[]>([""]);
  const [notesLoader, setNotesLoader] = useState<boolean>(false);
  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
  const [openUpdatePlan, setOpenUpdatePlan] = useState<boolean>(false);
  const [openDemoModal, setOpenDemoModal] = useState<boolean>(false);
  const [demoLoader, setDemoLoader] = useState<boolean>(false);

  useBreadcrumbs(
    {
      title: item
        ? `${item.first_name} ${item.last_name}`
        : "School Detail",
      path: routeConstant.demoRequestSchoolDetail.path.replace(":id", id || "")
    },
    true,
    item
  );

  const fetchDetail = () => {
    setPageLoader(true);
    getSchoolOverviewService(Number(id))
      .then(({ data }) => {
        const record = data?.data;
        setItem(record);

        const rawNotes = record?.notes;
        if (rawNotes) {
          try {
            const parsed = JSON.parse(rawNotes);
            setNotesList(
              Array.isArray(parsed) && parsed.length > 0 ? parsed : [rawNotes]
            );
          } catch {
            setNotesList([rawNotes]);
          }
        } else {
          setNotesList([""]);
        }
        setPageLoader(false);
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
        setPageLoader(false);
      });
  };

  // A single call returns every language + age combination (with the
  // available books_count). The language and age dropdowns are derived
  // from it, so the separate languages / age-ranges calls are no longer
  // needed.
  const fetchDropdowns = () => {
    getBookFilterCountsService()
      .then(({ data }) => {
        const payload = data?.data;
        // The endpoint used to return a bare array of combinations; it now
        // returns { counts, totals, languages, age_ranges }.
        const counts: any[] = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.counts)
            ? payload.counts
            : [];
        setBookFilterCounts(counts);

        const languageMap = new Map<number, any>();
        const ageMap = new Map<number, any>();
        counts.forEach((c) => {
          if (c?.language_id != null && !languageMap.has(c.language_id)) {
            languageMap.set(c.language_id, {
              id: c.language_id,
              name: c.language_name
            });
          }
          if (c?.age_range_id != null && !ageMap.has(c.age_range_id)) {
            ageMap.set(c.age_range_id, {
              id: c.age_range_id,
              text: c.age_range_label
            });
          }
        });
        setLanguages(Array.from(languageMap.values()));
        setAgeGroups(Array.from(ageMap.values()));
      })
      .catch((err) =>
        console.error("Book filter counts error:", err)
      );
  };

  useEffect(() => {
    fetchDetail();
    fetchDropdowns();
    // eslint-disable-next-line
  }, [id]);

  const handleNotesSubmit = () => {
    const filtered = notesList.filter((n) => n.trim() !== "");
    const notesJson = JSON.stringify(filtered);
    const formData = new FormData();
    formData.append("notes", notesJson);
    setNotesLoader(true);
    reviewDemoRequestService(Number(id), formData)
      .then(({ data }) => {
        toastMessage("success", data?.message);
        setNotesLoader(false);
        setItem((prev: any) => ({ ...prev, notes: notesJson }));
        setNotesList(filtered.length > 0 ? filtered : [""]);
        setOpenNotes(false);
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
        setNotesLoader(false);
      });
  };

  /**
   * Turn a live school into a demo one.
   *
   * There is no dedicated endpoint for this: `change-limit` is the single route
   * that writes a school's plan-side fields, so the flag rides along with it.
   * The two limits are resent unchanged because the endpoint rewrites the whole
   * record from what it is given — omitting them would reset them.
   */
  const handleConvertToDemo = () => {
    setDemoLoader(true);
    editLimit({
      school_id: item?.id,
      teachers_limit: item?.school?.teachers_limit,
      students_limit: item?.school?.students_limit,
      is_demo: 1
    })
      .then(({ data }) => {
        toastMessage("success", data?.message);
        setDemoLoader(false);
        setOpenDemoModal(false);
        fetchDetail();
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
        setDemoLoader(false);
      });
  };

  const handleReject = (reason: string) => {
    if (!reason?.trim()) return;
    const formData = new FormData();
    formData.append("status", "3");
    formData.append("rejection_reason", reason);
    setRejectLoader(true);
    reviewDemoRequestService(Number(id), formData)
      .then(({ data }) => {
        toastMessage("success", data?.message);
        setRejectLoader(false);
        setOpenRejectModal(false);
        fetchDetail();
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
        setRejectLoader(false);
      });
  };

  const status = statusConfig[item?.status];
  // The overview sends a real boolean, but change-limit takes 1/0 — read both.
  const isDemo = item?.is_demo === true || Number(item?.is_demo) === 1;
  /* Only a head office reports campus by campus. A school the health-stat
     response does not know about has no campuses to report on either, so it
     falls through to the standalone dashboard rather than to nothing. */
  const isHeadOffice = Number(analyticsSchool?.is_multi_campus) === 1;
  const analyticsName =
    analyticsSchool?.name || item?.school?.school_name || "School";
  const hasNotes = (() => {
    if (!item?.notes) return false;
    try {
      const parsed = JSON.parse(item.notes);
      return Array.isArray(parsed) && parsed.some((n: string) => n.trim());
    } catch {
      return Boolean(item.notes);
    }
  })();

  if (pageLoader) {
    return <SkeletonLoader />;
  }

  return (
    <div>
      {/* Info Section */}
      <Box className={baseStyles.infoCard}>
        <div className={baseStyles.infoTop}>
          <div className={baseStyles.infoLeft}>
            <div className={baseStyles.avatarCircle}>
              <PersonIcon />
            </div>
            <div className={baseStyles.infoMeta}>
              <div className={baseStyles.name}>
                {item?.school?.first_name} {item?.school?.last_name}
              </div>
              <div className={baseStyles.metaRow}>
                <div className={baseStyles.metaCol}>
                  <span className={baseStyles.metaLabel}>School</span>
                  <span className={baseStyles.metaValue}>
                    {item?.school?.school_name || "—"}
                  </span>
                </div>
                <div className={baseStyles.metaCol}>
                  <span className={baseStyles.metaLabel}>Role</span>
                  <span className={baseStyles.metaValue}>
                    {item?.school?.registrar_role || "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className={baseStyles.infoActions}>
            {/* A school can only go one way — once it is a demo the button has
                nothing left to do, so it gives way to a plain badge. */}
            {isDemo ? (
              <span className={styles.demoChip}>Demo School</span>
            ) : (
              <button
                type="button"
                className={styles.demoBtnOutlined}
                onClick={() => setOpenDemoModal(true)}
              >
                Convert to Demo
              </button>
            )}
            <button
              type="button"
              className={styles.updateBtnFilled}
              onClick={() => setOpenUpdatePlan(true)}
            >
              Update
            </button>
            {/* <button
              className={baseStyles.notesBtn}
              onClick={() => setOpenNotes(true)}
            >
              {hasNotes ? "Edit Notes" : "Add Notes"}
            </button>
            {status && (
              <span
                className={baseStyles.statusChip}
                style={{ backgroundColor: status.color }}
              >
                {status.label}
              </span>
            )} */}
          </div>
        </div>

        <hr className={baseStyles.divider} />

        <div className={baseStyles.infoIconRow}>
          <div className={baseStyles.infoIconItem}>
            <PhoneIcon className={baseStyles.infoIcon} />
            <span>{item?.school?.phone || "—"}</span>
          </div>
          <div className={baseStyles.infoIconItem}>
            <EmailIcon className={baseStyles.infoIcon} />
            <span>{item?.email || "—"}</span>
          </div>
          <div className={baseStyles.infoIconItem}>
            <GroupsIcon className={baseStyles.infoIcon} />
            <span>{item?.school?.students_limit ?? "—"} students</span>
          </div>
          <div className={baseStyles.infoIconItem}>
            <PersonOutlineIcon className={baseStyles.infoIcon} />
            <span>{item?.school?.teachers_limit ?? "—"} teachers</span>
          </div>
          <div className={baseStyles.infoIconItem}>
            <CalendarTodayIcon className={baseStyles.infoIcon} />
            <span>
              {item?.created_at
                ? moment(item.created_at).format("MMM DD, YYYY")
                : "—"}
            </span>
          </div>
        </div>
      </Box>

      {/* This school's reading analytics: the same dashboard it sees in its own
          panel, on the same school-API endpoints, scoped to its id. A head
          office reports campus by campus, a standalone school class by class,
          so the school row decides which one renders. */}
      <div className={styles.dashboardSection}>
        {analyticsLoading ? (
          <HeadOfficeDashboardSkeleton />
        ) : (
          <HeadOfficeThemeProvider>
            {isHeadOffice ? (
              <HeadOfficeDashboard schoolName={analyticsName} />
            ) : (
              <CampusDashboard
                schoolName={analyticsName}
                subtitle="School Dashboard"
              />
            )}
          </HeadOfficeThemeProvider>
        )}

        <PlanHistoryTable
          plans={plans}
          loading={plansLoading}
          page={plansPage}
          pageSize={HISTORY_PAGE_SIZE}
          total={plansTotal}
          onPageChange={setPlansPage}
          currency={currency}
          onViewPlan={(planId) => setOpenPlanId(String(planId))}
          onViewReceipt={(planId) => setOpenPlanReceiptId(String(planId))}
        />

        <PaymentRecordsTable
          payments={payments}
          loading={paymentsLoading}
          page={paymentsPage}
          pageSize={HISTORY_PAGE_SIZE}
          total={paymentsTotal}
          onPageChange={setPaymentsPage}
          currency={currency}
          onViewReceipt={setOpenReceiptId}
          onViewReason={setOpenReasonId}
          emptyLabel={
            plans.length
              ? "No payments recorded against this school's plans."
              : "This school has never held a plan, so there are no payments."
          }
        />
      </div>

      <ReceiptModal
        open={Boolean(openReceiptId)}
        receipt={openReceiptId ? receipts[openReceiptId] : undefined}
        onClose={() => setOpenReceiptId(null)}
      />

      <ReasonModal
        open={Boolean(openReasonId)}
        receipt={openReasonId ? receipts[openReasonId] : undefined}
        onClose={() => setOpenReasonId(null)}
      />

      <ReceiptModal
        open={Boolean(openPlanReceiptId)}
        receipt={
          openPlanReceiptId ? planReceipts[openPlanReceiptId] : undefined
        }
        onClose={() => setOpenPlanReceiptId(null)}
        title="Plan receipt"
      />

      <PlanCompareModal
        open={Boolean(openPlanId)}
        onClose={() => setOpenPlanId(null)}
        subtitle={openPlanId ? rawPlans[openPlanId]?.name : undefined}
        rows={openPlanId ? toCompareRows(rawPlans[openPlanId]) : []}
        campuses={openPlanId ? toCampuses(rawPlans[openPlanId]) : undefined}
      />

      {/* School Plan form has been moved into the UpdateSchoolPlan modal */}

      {/* Update Plan Modal */}
      <UpdateSchoolPlan
        open={openUpdatePlan}
        handleClose={() => setOpenUpdatePlan(false)}
        item={item}
        languages={languages}
        ageGroups={ageGroups}
        bookFilterCounts={bookFilterCounts}
        onSuccess={fetchDetail}
      />

      {/* Convert-to-Demo Modal */}
      <Confirmation
        open={openDemoModal}
        handleClose={() => setOpenDemoModal(false)}
        handleClick={handleConvertToDemo}
        loader={demoLoader}
        text={`Convert ${item?.school?.school_name || "this school"} into a demo school?`}
      />

      {/* Reject Modal */}
      <Confirmation
        open={openRejectModal}
        handleClose={() => setOpenRejectModal(false)}
        handleClick={handleReject}
        loader={rejectLoader}
        text={{ label: "Are you sure you want to reject this demo request?" }}
        showTextArea={true}
      />

      {/* Notes Drawer */}
      {/* <Drawer
        anchor="right"
        open={openNotes}
        onClose={() => setOpenNotes(false)}
      >
        <div className={baseStyles.drawerContainer}>
          <div className={baseStyles.drawerHeader}>
            <span className={baseStyles.drawerTitle}>Notes</span>
            <button
              className={baseStyles.drawerClose}
              onClick={() => setOpenNotes(false)}
            >
              <CloseIcon />
            </button>
          </div>

          <div className={baseStyles.drawerBody}>
            {notesList.map((note, index) => (
              <div key={index} className={baseStyles.noteItem}>
                <div className={baseStyles.noteLabelRow}>
                  <label className={baseStyles.label}>Note {index + 1}</label>
                  {notesList.length > 1 && (
                    <button
                      className={baseStyles.removeNoteBtn}
                      onClick={() =>
                        setNotesList((prev) =>
                          prev.filter((_, i) => i !== index)
                        )
                      }
                    >
                      <CloseIcon fontSize="small" />
                    </button>
                  )}
                </div>
                <textarea
                  className={baseStyles.textarea}
                  value={note}
                  onChange={(e) => {
                    const updated = [...notesList];
                    updated[index] = e.target.value;
                    setNotesList(updated);
                  }}
                  placeholder="Add note here..."
                />
              </div>
            ))}
            <button
              className={baseStyles.addNoteBtn}
              onClick={() => setNotesList((prev) => [...prev, ""])}
            >
              <AddIcon fontSize="small" /> Add Note
            </button>
          </div>

          <div className={baseStyles.drawerFooter}>
            <CustomButton
              text="Submit"
              buttonStyle={baseStyles.btnCustom}
              handleClick={handleNotesSubmit}
            />
            <CustomButton
              text="Cancel"
              buttonStyle={baseStyles.btnInsideOut}
              handleClick={() => setOpenNotes(false)}
            />
          </div>
        </div>
      </Drawer> */}

      {notesLoader && <Loader />}
    </div>
  );
}

export default DemoRequestSchoolDetail;
